import { useState, useCallback } from 'react';
import { backofficeManagementService } from '../services/backofficeManagementService';

export interface BackofficeInvoiceResponse {
  success: boolean;
  message: string;
  invoice_id?: string;
  invoice_number?: number;
  pdf_path?: string;
  download_url?: string;
  error?: string;
}

export interface BackofficeInvoiceItem {
  id: string;
  invoice_number: number;
  status: 'DRAFT' | 'POSTED' | 'PAID' | 'CLOSED';
  total_amount: number;
  tax_amount: number;
  currency: string;
  invoice_date: string | null;
  due_date: string | null;
  is_pdf_generated: boolean;
  order_id: string;
  created_at: string;
  updated_at: string;
}

export interface BackofficeInvoiceListResponse {
  items: BackofficeInvoiceItem[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export const useBackofficeInvoice = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [invoiceData, setInvoiceData] = useState<BackofficeInvoiceResponse | null>(null);
  const [invoicesList, setInvoicesList] = useState<BackofficeInvoiceItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  const getCustomerInvoices = useCallback(async (
    customerId: string,
    page: number = 1,
    size: number = 20,
    statusFilter?: string
  ): Promise<BackofficeInvoiceListResponse | null> => {
    setIsLoading(true);
    setError(null);
    setInfoMessage(null);
    try {
      const response = await backofficeManagementService.getCustomerInvoices(
        customerId,
        page,
        size,
        statusFilter
      );
      setInvoicesList(response.items);
      return response;
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to get customer invoices';
      console.error('Failed to get customer invoices:', error);
      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getOrderInvoice = useCallback(async (orderId: string): Promise<BackofficeInvoiceResponse | null> => {
    setIsLoading(true);
    setError(null);
    setInfoMessage(null);
    try {
      const response = await backofficeManagementService.getOrderInvoice(orderId);
      setInvoiceData(response);
      
      // Handle no invoice data case as informational message
      if (!response.success && response.error === 'no_invoice_data') {
        setInfoMessage(response.message);
      }
      
      return response;
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to get order invoice';
      console.error('Failed to get order invoice:', error);
      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const generateOrderInvoice = useCallback(async (
    orderId: string,
    forceRegenerate: boolean = false
  ): Promise<BackofficeInvoiceResponse | null> => {
    setIsGenerating(true);
    setError(null);
    setInfoMessage(null);
    try {
      const response = await backofficeManagementService.generateOrderInvoice(orderId, forceRegenerate);
      setInvoiceData(response);
      
      // Handle no invoice data case as informational message
      if (!response.success && response.error === 'no_invoice_data') {
        setInfoMessage(response.message);
      } else if (!response.success && response.message) {
        setError(response.message);
      }
      
      return response;
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to generate invoice';
      console.error('Failed to generate invoice:', error);
      setError(errorMessage);
      return null;
    } finally {
      setIsGenerating(false);
    }
  }, []);

  const downloadInvoice = useCallback(async (invoiceId: string): Promise<void> => {
    setError(null);
    try {
      await backofficeManagementService.downloadInvoice(invoiceId);
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to download invoice';
      console.error('Failed to download invoice:', error);
      setError(errorMessage);
      throw error;
    }
  }, []);

  const getDownloadUrl = useCallback((invoiceId: string): string => {
    return backofficeManagementService.getInvoiceDownloadUrl(invoiceId);
  }, []);

  const clearInvoiceData = useCallback(() => {
    setInvoiceData(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearInfoMessage = useCallback(() => {
    setInfoMessage(null);
  }, []);

  return {
    isLoading,
    isGenerating,
    invoiceData,
    invoicesList,
    error,
    infoMessage,
    getCustomerInvoices,
    getOrderInvoice,
    generateOrderInvoice,
    downloadInvoice,
    getDownloadUrl,
    clearInvoiceData,
    clearError,
    clearInfoMessage,
  };
};

export default useBackofficeInvoice;